// Presentation catalog for navigable resources. Keys MUST match the backend
// `Resource` enum (parity test in resources.test.ts). Per-role access (view/edit)
// comes from the backend role registry; this maps each resource to its area,
// route, icon and i18n label so the navbar can be built dynamically.

import {
  Brain,
  Building2,
  CalendarDays,
  ClipboardCheck,
  FileText,
  FolderKanban,
  Gavel,
  Layers,
  LayoutDashboard,
  Lightbulb,
  type LucideIcon,
  MapPin,
  Rocket,
  Rss,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Sprout,
  Target,
  Users,
} from 'lucide-react';
import { currentPlatform, ePath, fPath, gPath, scopedPath, tPath, type Platform } from '@/utils/paths';

export type Area =
  | 'terminus'
  | 'gaia'
  | 'foundershub'
  | 'org'
  | 'superadmin'
  | 'entrepreneur';

export interface ResourceMeta {
  key: string;
  area: Area;
  route: string;
  icon: LucideIcon;
  labelKey: string;
  // Resources sharing a groupId render nested under a collapsible submenu
  // (see GROUPS); ungrouped resources render as flat links.
  groupId?: string;
  // Kept in the catalog for backend permission parity, but intentionally not
  // shown in the navigation until its page is implemented.
  hiddenFromNavigation?: boolean;
  // La sección solo existe en esta plataforma. Sin el campo, existe en las
  // tres. Es navegación, no autorización: el StaffRoute de la ruta sigue
  // siendo la frontera.
  platform?: Platform;
}

export interface ResourceGroup {
  labelKey: string;
  icon: LucideIcon;
}

export const GROUPS: Record<string, ResourceGroup> = {
  research: { labelKey: 'sidebar.admin.researchCenter', icon: Brain },
};

export const RESOURCES: ResourceMeta[] = [
  // terminus
  { key: 'market_insights', area: 'terminus', route: tPath('/market-insights'), icon: FileText, labelKey: 'sidebar.admin.marketInsights', groupId: 'research' },
  { key: 'opportunities', area: 'terminus', route: tPath('/opportunities'), icon: Target, labelKey: 'sidebar.admin.opportunities' },
  { key: 'discovery', area: 'terminus', route: tPath('/discovery'), icon: Sparkles, labelKey: 'sidebar.admin.discovery', groupId: 'research' },
  { key: 'dealflow', area: 'terminus', route: tPath('/startups'), icon: Building2, labelKey: 'sidebar.admin.dealflow', groupId: 'research' },
  { key: 'committees', area: 'terminus', route: tPath('/committees'), icon: Gavel, labelKey: 'sidebar.admin.committee' },
  { key: 'project_leads', area: 'terminus', route: tPath('/project-leads'), icon: FolderKanban, labelKey: 'sidebar.admin.projectLeads' },
  { key: 'projects', area: 'terminus', route: tPath('/projects'), icon: FolderKanban, labelKey: 'sidebar.admin.projects' },
  { key: 'clients', area: 'terminus', route: tPath('/clients'), icon: Users, labelKey: 'sidebar.admin.clients' },
  // The founder-facing portal has no entry here on purpose: it is governed by
  // authentication, approval state and platform entitlement, never by the
  // configurable matrix. Its fixed navigation lives in `entrepreneurNav.ts`.
  // gaia
  { key: 'gaia_dashboard', area: 'gaia', route: gPath('/projects'), icon: LayoutDashboard, labelKey: 'gaia.sidebar.dashboard', hiddenFromNavigation: true },
  { key: 'gaia_projects', area: 'gaia', route: gPath('/projects'), icon: FolderKanban, labelKey: 'gaia.sidebar.projects' },
  { key: 'gaia_documents', area: 'gaia', route: gPath('/documents'), icon: FileText, labelKey: 'gaiaDocuments.sidebar' },
  { key: 'gaia_meetings', area: 'gaia', route: gPath('/meetings'), icon: CalendarDays, labelKey: 'gaia.sidebar.meetings' },
  { key: 'gaia_contracts', area: 'gaia', route: gPath('/projects'), icon: LayoutDashboard, labelKey: 'gaia.sidebar.contracts', hiddenFromNavigation: true },
  { key: 'gaia_budgets', area: 'gaia', route: gPath('/projects'), icon: LayoutDashboard, labelKey: 'gaia.sidebar.budgets', hiddenFromNavigation: true },
  { key: 'gaia_programs', area: 'gaia', route: gPath('/programs'), icon: Layers, labelKey: 'gaia.sidebar.programs' },
  { key: 'gaia_audit_log', area: 'gaia', route: gPath('/projects'), icon: LayoutDashboard, labelKey: 'gaia.sidebar.auditLog', hiddenFromNavigation: true },
  // No section of its own any more: the aggregated inbox was removed, the
  // notification bell is the inbox and a review is opened on the phase page.
  // The key survives as a seeded row of every role's resource access; it no
  // longer gates any endpoint (review is per activity).
  { key: 'gaia_evaluations', area: 'gaia', route: gPath('/projects'), icon: ClipboardCheck, labelKey: 'gaia.sidebar.evaluations', hiddenFromNavigation: true },
  // foundershub
  { key: 'fh_dashboard', area: 'foundershub', route: fPath('/'), icon: LayoutDashboard, labelKey: 'foundershub.sidebar.dashboard' },
  { key: 'fh_entrepreneurs', area: 'foundershub', route: fPath('/entrepreneurs'), icon: Users, labelKey: 'foundershub.sidebar.entrepreneurs' },
  { key: 'fh_projects', area: 'foundershub', route: fPath('/projects'), icon: FolderKanban, labelKey: 'foundershub.sidebar.projects' },
  // org — un árbol por plataforma. `organization` es solo la puerta exterior:
  // no tiene fila de menú propia, solo es destino del switcher.
  { key: 'organization', area: 'org', route: scopedPath('/org'), icon: Building2, labelKey: 'org.platform.nav', hiddenFromNavigation: true },
  { key: 'org_overview', area: 'org', route: scopedPath('/org/overview'), icon: LayoutDashboard, labelKey: 'org.sidebar.dashboard' },
  { key: 'org_team', area: 'org', route: scopedPath('/org/team'), icon: Users, labelKey: 'org.sidebar.team' },
  { key: 'org_users', area: 'org', route: scopedPath('/org/team'), icon: Users, labelKey: 'org.sidebar.team', hiddenFromNavigation: true },
  // Las tres comparten ruta a propósito — como org_users comparte la de
  // org_team. El panel es de UNA plataforma, así que el filtro de
  // `platform` deja viva exactamente una, y la ruta que renderiza es la del
  // panel en el que ya estás. Compartir `scopedPath` en vez de dar a cada
  // una su `platformPath` es lo que garantiza que ninguna entrada de menú
  // pueda ser una URL absoluta cross-host, que <Link> no traga.
  { key: 'org_platform_terminus', area: 'org', route: scopedPath('/org/platform'), icon: FileText, labelKey: 'org.sidebar.platformTerminus', platform: 'terminus' },
  { key: 'org_platform_foundershub', area: 'org', route: scopedPath('/org/platform'), icon: Rocket, labelKey: 'org.sidebar.platformFoundershub', platform: 'foundershub' },
  { key: 'org_platform_gaia', area: 'org', route: scopedPath('/org/platform'), icon: Sprout, labelKey: 'org.sidebar.platformGaia', platform: 'gaia' },
  { key: 'org_locations', area: 'org', route: scopedPath('/org/locations'), icon: MapPin, labelKey: 'org.sidebar.locations', platform: 'terminus' },
  { key: 'org_sources', area: 'org', route: scopedPath('/org/sources'), icon: Rss, labelKey: 'org.sidebar.sources', platform: 'terminus' },
  { key: 'sa_organizations', area: 'superadmin', route: scopedPath('/superadmin/organizations'), icon: Building2, labelKey: 'superadmin.sidebar.organizations' },
  { key: 'sa_users', area: 'superadmin', route: scopedPath('/superadmin/users'), icon: ShieldCheck, labelKey: 'superadmin.sidebar.users' },
  { key: 'sa_roles', area: 'superadmin', route: scopedPath('/superadmin/roles'), icon: ShieldAlert, labelKey: 'superadmin.sidebar.roles' },
];

export const RESOURCE_BY_KEY: Record<string, ResourceMeta> = Object.fromEntries(
  RESOURCES.map((r) => [r.key, r])
);

export const ALL_RESOURCE_KEYS: string[] = RESOURCES.map((r) => r.key);

// The route for the first resource in an already-resolved, permission-filtered
// key list (e.g. from accessibleResources), or `fallback` when the list is
// empty. Shared by DynamicSidebar's homeHref and DashboardRedirect so both
// "land the user somewhere real" computations stay in sync.
export function firstAccessibleRoute(keys: string[], fallback: string): string {
  return keys.length ? RESOURCE_BY_KEY[keys[0]].route : fallback;
}

// Catalog grouped by area (computed once) so renderers don't re-filter the
// full catalog on every render.
export const RESOURCES_BY_AREA: Record<Area, ResourceMeta[]> = RESOURCES.reduce(
  (acc, r) => {
    (acc[r.area] ??= []).push(r);
    return acc;
  },
  {} as Record<Area, ResourceMeta[]>
);

// Cross-area app-switcher metadata + the per-area brand for the sidebar header.
export interface AreaMeta {
  area: Area;
  brandLabel: string;
  switcherIcon: LucideIcon;
  switcherLabelKey: string;
  switcherHref: string;
  requiresFeature?: string;
}

export const AREA_META: Record<Area, AreaMeta> = {
  terminus: { area: 'terminus', brandLabel: 'terminus.', switcherIcon: FileText, switcherLabelKey: 'sidebar.terminusButton', switcherHref: tPath('/market-insights') },
  foundershub: { area: 'foundershub', brandLabel: "founder's hub.", switcherIcon: Rocket, switcherLabelKey: 'sidebar.foundersHubButton', switcherHref: fPath('/'), requiresFeature: 'foundershub' },
  gaia: { area: 'gaia', brandLabel: 'gaia.', switcherIcon: Sprout, switcherLabelKey: 'sidebar.gaiaButton', switcherHref: gPath('/projects'), requiresFeature: 'gaia' },
  // Los dos paneles pertenecen a la plataforma en la que estás: en el host de
  // un producto son `/org` y `/superadmin`, y en modo prefijo van prefijados.
  // El switcher te lleva al panel de TU plataforma, no a uno compartido.
  org: { area: 'org', brandLabel: 'organization.', switcherIcon: Settings2, switcherLabelKey: 'sidebar.organizationButton', switcherHref: scopedPath('/org') },
  superadmin: { area: 'superadmin', brandLabel: 'superadmin.', switcherIcon: ShieldAlert, switcherLabelKey: 'sidebar.superadminButton', switcherHref: scopedPath('/superadmin') },
  // Founder-facing portal. Role-gated (ENTREPRENEUR only) and deliberately NOT in
  // AREA_ORDER, so it never appears as a staff app-switcher target.
  entrepreneur: { area: 'entrepreneur', brandLabel: "founder's hub.", switcherIcon: Lightbulb, switcherLabelKey: 'sidebar.foundersHubButton', switcherHref: ePath('/dashboard') },
};

export const AREA_ORDER: Area[] = ['terminus', 'foundershub', 'gaia', 'org', 'superadmin'];

/** The brand a platform wears, or the neutral `vess.` for none. Every
 * unbranded auth screen (login, reset password, onboarding, legal) reads it
 * from here so a host cannot end up wearing another platform's name. */
export function platformBrandLabel(platform: Platform | null): string {
  return platform ? AREA_META[platform].brandLabel : 'vess.';
}

/** The brand of the surface we are on. */
export function currentBrandLabel(): string {
  return platformBrandLabel(currentPlatform());
}

/** Where a platform lands you. One table, read by `/`, the app switcher and
 * every 403 escape link. */
export function platformHome(platform: Platform | null): string {
  return platform ? AREA_META[platform].switcherHref : AREA_META.org.switcherHref;
}
